import { TreeNodeInfo } from '@blueprintjs/core';

export type TreeNodeId = TreeNodeInfo['id'];

type BaseNode<T = any> = { childNodes?: T[] | undefined };

export const filterEmptyNodes = <T extends BaseNode>(node: T) =>
  node.childNodes && node.childNodes.length > 0;

export const forEachNode = <T extends BaseNode<T>>(
  nodes: T[] | undefined,
  callback: (node: T, parentNodes: T[]) => void,
  parentNodes: T[] = []
) => {
  if (nodes === undefined) {
    return;
  }
  nodes.forEach((node) => {
    callback(node, parentNodes);
    forEachNode(node.childNodes, callback, [...parentNodes, node]);
  });
};

export const findMapNode = <T extends BaseNode<T>, R>(
  nodes: T[] | undefined,
  findMap: (node: T, parentNodes: T[]) => R | undefined,
  parentNodes: T[] = []
): R | undefined => {
  if (nodes === undefined) {
    return undefined;
  }

  // eslint-disable-next-line no-restricted-syntax
  for (const node of nodes) {
    let value = findMap(node, parentNodes);
    if (value !== undefined) {
      return value;
    }

    value = findMapNode(node.childNodes, findMap, [...parentNodes, node]);
    if (value !== undefined) {
      return value;
    }
  }

  return undefined;
};

export const findNode = <T extends BaseNode<T>>(
  nodes: T[] | undefined,
  find: (node: T) => boolean
): T | undefined => {
  if (nodes === undefined) {
    return undefined;
  }

  // eslint-disable-next-line no-restricted-syntax
  for (const node of nodes) {
    if (find(node)) {
      return node;
    }

    if (node.childNodes) {
      const found = findNode(node.childNodes, find);
      if (found) {
        return found;
      }
    }
  }

  return undefined;
};
